// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import { useState, useEffect } from "react";

import { generateClient } from "@aws-amplify/api";

import { UsageRecord, UsageSummary } from "../../../types/usageCostAllocation";
import { usageListRecords } from "../../../graphql/usageCostAllocation/queries";

const client = generateClient({ authMode: "userPool" });

export interface UseUsageRecordsResult {
	records: UsageRecord[];
	summary: UsageSummary;
	loading: boolean;
	error: string | null;
}

export function computeUsageSummary(records: UsageRecord[]): UsageSummary {
	let totalCharacterCount = 0;
	let totalEstimatedCost = 0;

	for (const record of records) {
		totalCharacterCount += record.metricValue;
		totalEstimatedCost += record.estimatedCost;
	}

	return { totalCharacterCount, totalEstimatedCost };
}

export function sortRecordsByTimestampDesc(records: UsageRecord[]): UsageRecord[] {
	return [...records].sort((a, b) => b.timestamp - a.timestamp);
}

export function useUsageRecords(billingPeriod: string): UseUsageRecordsResult {
	const [records, setRecords] = useState<UsageRecord[]>([]);
	const [summary, setSummary] = useState<UsageSummary>({
		totalCharacterCount: 0,
		totalEstimatedCost: 0,
	});
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		async function fetchRecords() {
			setLoading(true);
			setError(null);
			try {
				const response = await client.graphql({
					query: usageListRecords,
					variables: { billingPeriod },
				});
				const result = (response as any).data.usageListRecords;
				const data: UsageRecord[] =
					(Array.isArray(result) ? result : result?.items) ?? [];
				const sorted = sortRecordsByTimestampDesc(data);
				setRecords(sorted);
				setSummary(computeUsageSummary(sorted));
			} catch (err: any) {
				console.error("Failed to fetch usage records", err);
				setError(err?.message ?? "Failed to fetch usage records");
			} finally {
				setLoading(false);
			}
		}
		fetchRecords();
	}, [billingPeriod]);

	return { records, summary, loading, error };
}
